import {
  Car,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  CreditCard,
  DollarSign,
  Luggage,
  Navigation,
  Phone,
  Plane,
  Shield,
  Star,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { Airport, AirportsService } from "@/services/landing/airportsService";
import {
  fetchRideOptions,
  RideOption,
} from "@/services/landing/catchRidesService";
import { fetchHeroBackgrounds } from "@/services/landing/heroBackgroundService";

export default function AirportServices() {
  const navigate = useNavigate();
  const [showQR, setShowQR] = useState(false);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [loading, setLoading] = useState(true);
  const [vehicleTypes, setVehicleTypes] = useState<RideOption[]>([]);
  const [ridesLoading, setRidesLoading] = useState(true);
  const [airportSlideIndex, setAirportSlideIndex] = useState(0);
  const [ridesCurrentPage, setRidesCurrentPage] = useState(1);
  const ridesPerPage = 6;

  const [heroBackground, setHeroBackground] = useState<null | {
    imageAlt: string;
    imageUrl: string;
  }>(null);
  const [heroLoading, setHeroLoading] = useState(true);

  useEffect(() => {
    const fetchAirports = async () => {
      try {
        setLoading(true);
        const data = await AirportsService.getAll();
        // Filter active airports (not hidden)
        const activeAirports = data.filter((airport) => !airport.hidden);
        setAirports(activeAirports);
      } catch (error) {
        console.error("Failed to load airports:", error);
        setAirports([]);
      } finally {
        setLoading(false);
      }
    };

    const fetchRides = async () => {
      try {
        setRidesLoading(true);
        const rides = await fetchRideOptions();
        setVehicleTypes(rides);
      } catch (error) {
        console.error("Failed to load ride options:", error);
        setVehicleTypes([]);
      } finally {
        setRidesLoading(false);
      }
    };

    fetchAirports();
    fetchRides();

    fetchHeroBackgrounds("airports").then((data) => {
      setHeroBackground(data);
      setHeroLoading(false);
    });
  }, []);

  // Airport slider handlers
  const handleAirportNext = () => {
    setAirportSlideIndex((prev) =>
      prev + 3 >= airports.length ? 0 : prev + 3,
    );
  };

  const handleAirportPrev = () => {
    setAirportSlideIndex((prev) =>
      prev === 0 ? Math.max(0, airports.length - 3) : prev - 3,
    );
  };

  // Rides pagination handlers
  const totalRidesPages = Math.ceil(vehicleTypes.length / ridesPerPage);
  const indexOfLastRide = ridesCurrentPage * ridesPerPage;
  const indexOfFirstRide = indexOfLastRide - ridesPerPage;
  const currentRides = vehicleTypes.slice(indexOfFirstRide, indexOfLastRide);

  const handleRidesPageChange = (pageNumber: number) => {
    setRidesCurrentPage(pageNumber);
    // Scroll to rides section
    document
      .getElementById("choose-your-ride")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const features = [
    {
      description: "Airport pickups and drop-offs available around the clock",
      icon: <Clock className="text-primary" size={32} />,
      title: "24/7 Availability",
    },
    {
      description: "No surge pricing - know your fare before you book",
      icon: <Shield className="text-success" size={32} />,
      title: "Fixed Pricing",
    },
    {
      description: "Track your driver from pickup to destination",
      icon: <Navigation className="text-info" size={32} />,
      title: "Real-time Tracking",
    },
    {
      description: "Drivers help with loading and unloading your bags",
      icon: <Luggage className="text-warning" size={32} />,
      title: "Luggage Assistance",
    },
  ];

  const bookingSteps = [
    {
      description: "Choose your departure or arrival airport",
      icon: <Plane className="text-primary" size={24} />,
      step: "1",
      title: "Select Airport Service",
    },
    {
      description: "Add your flight number for accurate timing",
      icon: <Clock className="text-secondary" size={24} />,
      step: "2",
      title: "Enter Flight Details",
    },
    {
      description: "Select based on your group size and luggage",
      icon: <Car className="text-accent" size={24} />,
      step: "3",
      title: "Choose Vehicle Type",
    },
    {
      description: "Complete booking with upfront pricing",
      icon: <CreditCard className="text-success" size={24} />,
      step: "4",
      title: "Confirm & Pay",
    },
  ];

  const tips = [
    "Allow extra time during peak travel seasons",
    "Add your flight number for automatic delay tracking",
    "Meet your driver at the designated pickup area",
    "Keep your phone charged for driver communication",
    "Have your booking reference ready",
  ];

  return (
    <>
      <AppNavbar />

      <div className="min-h-screen bg-base-200">
        <Breadcrumbs />

        {/* Hero Section */}
        <section className="relative -mt-16 overflow-hidden pt-16">
          <div className="absolute inset-0 opacity-20">
            {heroLoading ? (
              <div className="relative h-full w-full overflow-hidden bg-base-200">
                <div
                  className="animate-shimmer absolute inset-0 bg-gradient-to-r from-base-200 via-base-100 to-base-200"
                  style={{ backgroundSize: "200% 100%" }}
                />
              </div>
            ) : heroBackground?.imageUrl ? (
              <img
                alt={heroBackground?.imageAlt || "Hero background"}
                className="h-full w-full object-cover"
                src={heroBackground.imageUrl}
              />
            ) : null}
          </div>

          <div className="relative mx-auto max-w-7xl px-4 py-24">
            <div className="max-w-4xl">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-info-content/20 px-4 py-2">
                <Plane size={20} />
                <span className="font-medium">
                  {loading
                    ? "Loading..."
                    : `Serving ${airports.length} Major Airports`}
                </span>
              </div>

              <h1 className="mb-6 text-5xl font-bold leading-tight lg:text-6xl">
                Reliable airport transfers{" "}
                <span className="text-primary">you can count on</span>
              </h1>

              <p className="mb-8 text-xl leading-relaxed opacity-90">
                Start or end your journey stress-free with Hareeng s dedicated
                airport service. Fixed pricing, professional drivers, and 24/7
                availability across Africas busiest airports.
              </p>

              <div className="flex flex-col gap-4 sm:flex-row">
                {/* <button
                  className="btn btn-primary btn-lg"
                  onClick={() => setShowQR(true)}
                >
                  <Plane size={20} />
                  Book Airport Ride
                </button>
                <button
                  className="btn btn-outline btn-lg"
                  onClick={() => {
                    document
                      .getElementById("airport-locations")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  <span className="flex items-center gap-2">
                    <DollarSign size={20} />
                    View Pricing
                  </span>
                </button> */}
              </div>

              <div className="mt-8 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold">
                    {loading ? "..." : airports.length}
                  </div>
                  <div className="text-sm opacity-80">Major Airports</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">24/7</div>
                  <div className="text-sm opacity-80">Service Available</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">
                    {loading
                      ? "..."
                      : airports.length > 0
                        ? `${airports[0].average_wait_time}min`
                        : "15min"}
                  </div>
                  <div className="text-sm opacity-80">Average Pickup</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* QR Code Popup */}
        {showQR && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={() => setShowQR(false)}
          >
            <div
              className={`relative rounded-2xl bg-base-100 p-8 text-base-content shadow-2xl dark:bg-base-300`}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                aria-label="Close"
                className="btn btn-circle btn-ghost btn-sm absolute right-4 top-4"
                onClick={() => setShowQR(false)}
              >
                ✕
              </button>
              <h3 className="mb-4 text-center text-2xl font-bold">
                Download Hareeng App
              </h3>
              <div className="flex flex-col items-center gap-4">
                <img
                  alt="QR code to download Hareeng app"
                  className="rounded-lg border border-base-300 bg-base-200"
                  src="https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=https://hareeng.com/app"
                />
                <div className="text-center text-base-content/70">
                  Scan to download the app for airport rides.
                  <br />
                  <a
                    className="link link-primary"
                    href="https://hareeng.com/app"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    Or tap here to download
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Airport Locations */}
        <section className="py-16" id="airport-locations">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Airport Locations</h2>
              <p className="text-xl text-base-content/70">
                Convenient pickups and drop-offs at major African airports
              </p>
            </div>

            {loading ? (
              <div className="py-8 text-center">
                <div className="loading loading-spinner loading-lg text-primary"></div>
                <p className="mt-4 text-base-content/70">Loading airports...</p>
              </div>
            ) : airports.length > 0 ? (
              <>
                {airports.length > 3 ? (
                  <div className="relative">
                    {/* Previous Button */}
                    <button
                      className="btn btn-circle btn-primary absolute left-0 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2"
                      onClick={handleAirportPrev}
                    >
                      <ChevronLeft size={24} />
                    </button>

                    {/* Slider Container */}
                    <div className="grid gap-8 lg:grid-cols-3">
                      {airports
                        .slice(airportSlideIndex, airportSlideIndex + 3)
                        .map((airport) => (
                          <div
                            className="card bg-base-100 shadow-xl"
                            key={airport.id}
                          >
                            <figure className="h-48">
                              <img
                                alt={airport.image_alt_text || airport.name}
                                className="h-full w-full object-cover"
                                src={`${import.meta.env.VITE_API_STORAGE_URL}/storage/${airport.image_url}`}
                              />
                            </figure>

                            <div className="card-body">
                              <div className="mb-2 flex items-center justify-between">
                                <h3 className="card-title">{airport.name}</h3>
                                <span className="badge">
                                  {airport.iata_code}
                                </span>
                              </div>

                              <p className="mb-4 text-base-content/70">
                                {airport.city}, {airport.country} • Avg wait:{" "}
                                {airport.average_wait_time} min
                              </p>

                              <div className="mb-4">
                                <h4 className="mb-2 font-semibold">
                                  Services:
                                </h4>
                                <div className="flex flex-wrap gap-1">
                                  {airport.services_available.map(
                                    (service, sIndex) => (
                                      <span
                                        className="badge badge-sm"
                                        key={sIndex}
                                      >
                                        {service.charAt(0).toUpperCase() +
                                          service.slice(1)}
                                      </span>
                                    ),
                                  )}
                                </div>
                              </div>

                              <div className="mb-4">
                                <h4 className="mb-2 font-semibold">
                                  Operating Hours:
                                </h4>
                                <p className="text-sm text-base-content/70">
                                  {airport.opening_time} -{" "}
                                  {airport.closing_time}{" "}
                                  {airport.timezone && `(${airport.timezone})`}
                                </p>
                              </div>

                              <div className="mb-4">
                                <h4 className="mb-2 font-semibold">
                                  Location:
                                </h4>
                                <p className="text-sm text-base-content/70">
                                  Lat: {airport.latitude.toFixed(4)}, Lng:{" "}
                                  {airport.longitude.toFixed(4)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>

                    {/* Next Button */}
                    <button
                      className="btn btn-circle btn-primary absolute right-0 top-1/2 z-10 -translate-y-1/2 translate-x-1/2"
                      onClick={handleAirportNext}
                    >
                      <ChevronRight size={24} />
                    </button>
                  </div>
                ) : (
                  <div className="grid gap-8 lg:grid-cols-3">
                    {airports.map((airport) => (
                      <div
                        className="card bg-base-100 shadow-xl"
                        key={airport.id}
                      >
                        <figure className="h-48">
                          <img
                            alt={airport.image_alt_text || airport.name}
                            className="h-full w-full object-cover"
                            src={`${import.meta.env.VITE_API_STORAGE_URL}/storage/${airport.image_url}`}
                          />
                        </figure>

                        <div className="card-body">
                          <div className="mb-2 flex items-center justify-between">
                            <h3 className="card-title">{airport.name}</h3>
                            <span className="badge">{airport.iata_code}</span>
                          </div>

                          <p className="mb-4 text-base-content/70">
                            {airport.city}, {airport.country} • Avg wait:{" "}
                            {airport.average_wait_time} min
                          </p>

                          <div className="mb-4">
                            <h4 className="mb-2 font-semibold">Services:</h4>
                            <div className="flex flex-wrap gap-1">
                              {airport.services_available.map(
                                (service, sIndex) => (
                                  <span className="badge badge-sm" key={sIndex}>
                                    {service.charAt(0).toUpperCase() +
                                      service.slice(1)}
                                  </span>
                                ),
                              )}
                            </div>
                          </div>

                          <div className="mb-4">
                            <h4 className="mb-2 font-semibold">
                              Operating Hours:
                            </h4>
                            <p className="text-sm text-base-content/70">
                              {airport.opening_time} - {airport.closing_time}{" "}
                              {airport.timezone && `(${airport.timezone})`}
                            </p>
                          </div>

                          <div className="mb-4">
                            <h4 className="mb-2 font-semibold">Location:</h4>
                            <p className="text-sm text-base-content/70">
                              Lat: {airport.latitude.toFixed(4)}, Lng:{" "}
                              {airport.longitude.toFixed(4)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="py-8 text-center">
                <p className="text-base-content/70">
                  No airports available at this time.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Vehicle Types */}
        <section className="bg-base-100 py-16" id="choose-your-ride">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">Choose Your Ride</h2>
              <p className="text-xl text-base-content/70">
                Select the perfect vehicle for your airport transfer
              </p>
            </div>

            {ridesLoading ? (
              <div className="py-8 text-center">
                <div className="loading loading-spinner loading-lg text-primary"></div>
                <p className="mt-4 text-base-content/70">
                  Loading ride options...
                </p>
              </div>
            ) : vehicleTypes.length > 0 ? (
              <>
                <div className="grid gap-8 md:grid-cols-3">
                  {(vehicleTypes.length > 6 ? currentRides : vehicleTypes).map(
                    (vehicle) => {
                      // Parse features - handle both JSON string and array
                      let vehicleFeatures: string[] = [];
                      if (Array.isArray(vehicle.features)) {
                        vehicleFeatures = vehicle.features;
                      } else if (typeof vehicle.features === "string") {
                        try {
                          vehicleFeatures = JSON.parse(vehicle.features);
                        } catch (e) {
                          console.error("Failed to parse features:", e);
                          vehicleFeatures = [];
                        }
                      }

                      const icon = vehicle.title
                        .toLowerCase()
                        .includes("comfort") ? (
                        <Star className="text-secondary" size={32} />
                      ) : vehicle.title.toLowerCase().includes("xl") ? (
                        <Users className="text-accent" size={32} />
                      ) : (
                        <Car className="text-primary" size={32} />
                      );

                      return (
                        <div
                          className="card bg-base-200 shadow-lg"
                          key={vehicle.id}
                        >
                          <div className="card-body text-center">
                            <div className="mx-auto mb-4">{icon}</div>
                            <h3 className="card-title justify-center">
                              {vehicle.title}
                            </h3>
                            <p className="mb-4 text-base-content/80">
                              {vehicle.description}
                            </p>

                            <div className="mb-4 space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span>Capacity:</span>
                                <span className="font-semibold">
                                  {vehicle.capacity} passengers
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span>Est. Time:</span>
                                <span className="font-semibold">
                                  {vehicle.estimated_time || "N/A"}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span>From:</span>
                                <span className="font-semibold text-primary">
                                  R{vehicle.price}
                                </span>
                              </div>
                            </div>

                            <div className="text-left">
                              <h4 className="mb-2 text-sm font-semibold">
                                Features:
                              </h4>
                              <ul className="space-y-1 text-xs">
                                {vehicleFeatures.map((feature, fIndex) => (
                                  <li
                                    className="flex items-center gap-2"
                                    key={fIndex}
                                  >
                                    <CheckCircle
                                      className="text-success"
                                      size={12}
                                    />
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      );
                    },
                  )}
                </div>

                {/* Pagination */}
                {vehicleTypes.length > 6 && (
                  <div className="mt-12 flex justify-center">
                    <div className="join">
                      <button
                        className="btn join-item"
                        disabled={ridesCurrentPage === 1}
                        onClick={() =>
                          handleRidesPageChange(ridesCurrentPage - 1)
                        }
                      >
                        «
                      </button>
                      {Array.from(
                        { length: totalRidesPages },
                        (_, i) => i + 1,
                      ).map((page) => (
                        <button
                          className={`btn join-item ${ridesCurrentPage === page ? "btn-active" : ""}`}
                          key={page}
                          onClick={() => handleRidesPageChange(page)}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        className="btn join-item"
                        disabled={ridesCurrentPage === totalRidesPages}
                        onClick={() =>
                          handleRidesPageChange(ridesCurrentPage + 1)
                        }
                      >
                        »
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="py-8 text-center">
                <p className="text-base-content/70">
                  No ride options available at this time.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Features */}
        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">
                Why Choose Hareeng for Airports?
              </h2>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {features.map((feature, index) => (
                <div className="text-center" key={index}>
                  <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-base-100 shadow-lg">
                    {feature.icon}
                  </div>
                  <h3 className="mb-2 text-xl font-semibold">
                    {feature.title}
                  </h3>
                  <p className="text-base-content/70">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How to Book */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold">How to Book</h2>
              <p className="text-xl text-base-content/70">
                Simple steps to secure your airport transfer
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {bookingSteps.map((step, index) => (
                <div className="text-center" key={index}>
                  <div className="relative mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary text-primary-content">
                    <span className="text-2xl font-bold">{step.step}</span>
                    <div className="absolute -bottom-2 -right-2 rounded-full bg-base-100 p-2">
                      {step.icon}
                    </div>
                  </div>
                  <h3 className="mb-2 text-xl font-semibold">{step.title}</h3>
                  <p className="text-base-content/70">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Calculator */}
        {/* <section className="py-16">
          <div className="mx-auto max-w-4xl px-4">
            <div className="card bg-base-100 shadow-2xl">
              <div className="card-body">
                <h2 className="card-title mb-6 justify-center text-2xl">
                  Estimate Your Fare
                </h2>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">From</span>
                    </label>
                    <select className="select select-bordered">
                      <option>Select Airport</option>
                      <option>O.R. Tambo (JNB)</option>
                      <option>Cape Town (CPT)</option>
                      <option>King Shaka (DUR)</option>
                    </select>
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">To</span>
                    </label>
                    <input
                      className="input input-bordered"
                      placeholder="Enter destination"
                      type="text"
                    />
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Vehicle Type</span>
                    </label>
                    <select className="select select-bordered">
                      <option>Hareeng Go</option>
                      <option>Hareeng Comfort</option>
                      <option>Hareeng XL</option>
                    </select>
                  </div>

                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Date & Time</span>
                    </label>
                    <input
                      className="input input-bordered"
                      type="datetime-local"
                    />
                  </div>
                </div>

                <div className="card-actions mt-6">
                  <button className="btn btn-primary w-full">
                    Get Fare Estimate
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section> */}

        {/* Travel Tips */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div>
                <h2 className="mb-6 text-4xl font-bold">Airport Travel Tips</h2>
                <p className="mb-6 text-lg text-base-content/70">
                  Make your airport transfer smooth and stress-free with these
                  helpful tips.
                </p>

                <ul className="space-y-3">
                  {tips.map((tip, index) => (
                    <li className="flex items-start gap-3" key={index}>
                      <CheckCircle
                        className="mt-1 flex-shrink-0 text-success"
                        size={16}
                      />
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="relative">
                <div className="card border border-primary/20 bg-primary/10">
                  <div className="card-body">
                    <h3 className="card-title mb-4 text-primary">Need Help?</h3>
                    <p className="mb-4">
                      Our airport support team is available 24/7 to assist with
                      your booking and any travel questions.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <Phone className="text-primary" size={16} />
                        <span className="font-semibold">
                          Click Button Below
                        </span>
                      </div>
                      <div className="text-sm text-base-content/70">
                        Airport Support Hotline
                      </div>
                    </div>
                    <div className="card-actions mt-4">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => navigate("/contact-us")}
                      >
                        Contact Us
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
